import { useDispatch, useSelector } from 'react-redux'
import { useState, useEffect, useMemo } from 'react'
import { teamService } from '../../services/team'
import { setUpperPopup, setPopup } from '../../store/reducers/general'
import { editTeam, deleteTeam } from '../../store/reducers/user'
import { rolesTranslations } from '../../services/data'
import { SendInvite } from './send-invite'
import { rolesColors } from '../../services/data'
import { ReassignMissions } from './reassign-missions.jsx'
import { Login } from '../../pages/login.jsx'

export function Members({ teamVideos }) {
    const dispatch = useDispatch()
    const user = useSelector(state => state.user)
    const team = useSelector(state => state.user.currentTeam)
    const [selectedMember, setSelectedMember] = useState('')
    const [isEdit, setIsEdit] = useState(false)
    const [isOpen, setIsOpen] = useState(false)
    const [isDelete, setIsDelete] = useState(false)
    const [popupDisplay, setPopupDisplay] = useState('')
    const [roles, setRoles] = useState({
        ideaMaker: false,
        thumbnailEditor: false,
        researcher: false,
        scripter: false,
        producer: false,
    })
    const userRoles = team?.members?.find(member => member._id === user._id)?.roles || []
    const isAdmin = userRoles.includes('admin')

    useEffect(() => {
        if (isAdmin) setIsDelete(false)
    }, [team.channelName])

    const deleteInvite = async (inviteToId) => {
        try {
            await teamService.deleteInvite(inviteToId, team._id)
            let invites = { ...team.invitesTo }
            delete invites[inviteToId]
            const newTeam = { ...team, invitesTo: invites }
            dispatch(editTeam(newTeam))
        }
        catch (err) {
            console.log(err)
            dispatch(setUpperPopup('errorServer'))
        }
    }

    const sendInvite = async (email, roles) => {
        try {
            const formatted = email.trim().toLowerCase()
            if (formatted === user.email) return dispatch(setUpperPopup('cant-invite-self'))
            const selectedRoles = Object.entries(roles).filter(([_, isChecked]) => isChecked).map(([role]) => role)
            if (!selectedRoles.length) return dispatch(setUpperPopup('no-roles-selected'))
            if (selectedRoles.length > 1 && selectedRoles.includes('thumbnailEditor')) return dispatch(setUpperPopup('thumbnail-editor-only'))
            const invite = await teamService.sendInvite(formatted, selectedRoles, team._id)
            const newTeam = { ...team, invitesTo: { ...team.invitesTo, [invite.recieverId]: invite } }
            dispatch(editTeam(newTeam))
            dispatch(setUpperPopup('invite-sent'))
            return true
        }
        catch (err) {
            console.log(err)
            if (err.message.includes('409')) dispatch(setUpperPopup('already-in-team'))
            else dispatch(setUpperPopup('errorServer'))
            return false
        }
    }

    const generateLink = (invite) => {
        const regex = /upload\/[^\/]+\/([^\/]+)\./
        const imageId = team.channelImage.match(regex)
        const link = `${window.location.origin}/invite/${team._id}/${team.channelName}/${imageId[1]}/${invite.roles.join('-')}/${invite.recieverId}`
        navigator.clipboard.writeText(link)
        dispatch(setUpperPopup('copied'))
    }

    const removeTeamMember = async (_id, vids) => {
        try {
            await teamService.deleteTeamMember(_id, team._id, vids)
            const newTeam = { ...team, members: team.members.filter(member => member._id !== _id) }
            setSelectedMember('')
            dispatch(editTeam(newTeam))
        }
        catch {
            dispatch(setUpperPopup('errorServer'))
        }
    }

    const handleToggle = (role) => {
        setRoles(prev => ({ ...prev, [role]: !prev[role] }))
    }

    const onEditClick = (member) => {
        setRoles({
            ideaMaker: member.roles.includes('ideaMaker'),
            thumbnailEditor: member.roles.includes('thumbnailEditor'),
            researcher: member.roles.includes('researcher'),
            scripter: member.roles.includes('scripter'),
            producer: member.roles.includes('producer'),
        })
        setIsEdit(true)
    }

    const editTeamMember = async () => {
        try {
            const selectedRoles = Object.entries(roles).filter(([_, isChecked]) => isChecked).map(([role]) => role)
            if (!selectedRoles.length) return dispatch(setUpperPopup('no-roles-selected'))
            if (selectedRoles.length > 1 && selectedRoles.includes('thumbnailEditor')) return dispatch(setUpperPopup('thumbnail-editor-only'))
            const editedMember = await teamService.editMemberRole(selectedMember._id, team._id, selectedRoles)
            const newTeam = { ...team, members: team.members.map(member => member._id === selectedMember._id ? editedMember : member) }
            dispatch(editTeam(newTeam))
            setIsEdit(false)
            setSelectedMember('')
        }
        catch (err) {
            console.log(err)
            dispatch(setUpperPopup('errorServer'))
        }
    }

    const onTeamDelete = async () => {
        try {
            await teamService.deleteTeam(team._id)
            dispatch(deleteTeam(team._id))
        }
        catch (err) {
            console.log(err)
            dispatch(setUpperPopup('errorServer'))
        }
    }

    const inDepartments = useMemo(() => {
        let inDepartments = {}
        Object.values(team.departments).forEach(department => {
            Object.values(department.members).forEach(member => {
                if (!inDepartments[member._id]) inDepartments[member._id] = [department.name]
                else inDepartments[member._id].push(department.name)
            })
        })
        return inDepartments
    }, [team.members, team.departments])

    const invitesToRender = Object.values(team?.invitesTo || {})

    if (!user._id) return <Login />

    return <div className="profile team">
        {team.members.length > 0 && <>
            <h2>חברי הצוות שלי</h2>
            <table className='members'>
                <thead>
                    <tr>
                        <td>שם</td>
                        <td>אימייל</td>
                        <td><span className='material-symbols-outlined clickable' onClick={() => dispatch(setPopup('roles'))}>info</span> תפקידים</td>
                        <td>מחלקות</td>
                        {isAdmin && <td>כמות משימות</td>}
                        {isAdmin && <td>פעולות</td>}
                    </tr>
                </thead>
                <tbody>
                    {team.members.map(member => <tr key={member.email}>
                        <td>{member.name}</td>
                        <td>{member.email}</td>
                        <td>
                            <div className='roles-wrapper'>
                                {member.roles.map(role => <div key={role} className='role-wrapper'>
                                    {role === 'admin' ? <img src={require('../../style/imgs/crown.png')} /> : <div className={rolesColors[role] ? rolesColors[role] : ''} />}
                                    <span>{rolesTranslations[role]}</span>
                                </div>)}
                            </div>
                        </td>
                        <td>
                            {inDepartments[member._id]?.map(d => <p key={d} className='red-text'>{d}</p>)}
                        </td>
                        {isAdmin && <td>{teamVideos[member._id]?.length || 0}</td>}
                        {isAdmin && <td>
                            {!member.roles.includes('admin') && <span className="material-symbols-outlined clickable" onClick={() => setSelectedMember(member)}>more_horiz</span>}
                            {selectedMember._id === member._id && <>
                                <div className='screen' onClick={() => setSelectedMember('')} style={{ zIndex: 4 }} />
                                <div className='card-menu appear'>
                                    {isEdit ?
                                        <div style={{ marginBottom: '20px', width: '100%', display: 'flex', flexDirection: 'column' }}>
                                            {Object.entries(roles).map(([role, checked]) => (
                                                <label key={role} className={rolesColors[role] ? rolesColors[role] + '-text' : ''}>
                                                    <input
                                                        type="checkbox"
                                                        checked={checked}
                                                        onChange={() => handleToggle(role)}
                                                    />
                                                    {rolesTranslations[role]}
                                                </label>
                                            ))}
                                            <div className='split'>
                                                <button className='main3' onClick={() => setIsEdit(false)} >בטל</button>
                                                <button className='back-main7' onClick={editTeamMember}>שמור</button>
                                            </div>
                                        </div> :
                                        <>
                                            <div> <span className="material-symbols-outlined">edit</span> <span onClick={() => onEditClick(member)}> ערוך תפקידים</span></div>
                                            {teamVideos[member._id]?.length && <div> <span className="material-symbols-outlined">visibility</span> <span onClick={() => setPopupDisplay('tasks')}> הצג משימות</span></div>}
                                            <div> <span className="material-symbols-outlined">block</span> <span onClick={() => setPopupDisplay('delete')}>הסר מהצוות</span></div>
                                            <div> <span className="material-symbols-outlined">cancel</span><span onClick={() => setSelectedMember('')}> סגור</span></div>
                                        </>}
                                </div>
                            </>}
                        </td>}
                    </tr>)}
                </tbody>
            </table>
        </>}

        {(invitesToRender.length > 0 && isAdmin) && <>
            <h2>הזמנות ששלחתי</h2>
            <table>
                <thead>
                    <tr>
                        <td>אימייל</td>
                        <td>תפקידים</td>
                        <td>סטטוס</td>
                        <td>פעולות</td>
                    </tr>
                </thead>
                <tbody>
                    {invitesToRender.map(invite => <tr key={invite.recieverId}>
                        <td>{invite.to}</td>
                        <td>{invite.roles.map(role => <span key={role}> {rolesTranslations[role]}</span>)}</td>
                        <td>{invite.status}</td>
                        <td className='invite-actions'>
                            <div className='clickable' onClick={() => generateLink(invite)}>
                                <span className="material-symbols-outlined">content_copy</span>
                                <span>העתק קישור</span>
                            </div>
                            <div className='clickable' onClick={() => deleteInvite(invite.recieverId)}>
                                <span className="material-symbols-outlined">delete</span>
                                <span>מחק הזמנה</span>
                            </div>
                        </td>
                    </tr>)}
                </tbody>
            </table>
        </>}

        {isAdmin && <h2 className='clickable noselect btn arise' onClick={() => setIsOpen(!isOpen)}>{isOpen ? 'סגור' : 'הזמן'}<span className='material-symbols-outlined'>{isOpen ? 'expand_less' : 'add'}</span></h2>}

        {isOpen && isAdmin && <SendInvite openRoles={() => dispatch(setPopup('roles'))} sendInvite={sendInvite} />}

        {isAdmin ? isDelete ? <button className="delete" onClick={onTeamDelete}>לחץ כאן למחיקה סופית</button> : <p className="delete-line" onClick={() => setIsDelete(true)}>מחק צוות</p> : null}

        {popupDisplay && <ReassignMissions closePopup={() => setPopupDisplay('')} member={selectedMember} vids={teamVideos[selectedMember._id]} team={team} removeTeamMember={removeTeamMember} transfer={popupDisplay === 'delete'} />}
    </div>
}